import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { TableCell, Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const ITEM_TYPE = 'COLUMN_HEADER';

/**
 * A draggable <TableCell> for reordering columns.
 * Props:
 *   index     — current position in columns array
 *   column    — ColumnDef
 *   onMove    — (fromIndex, toIndex) => void
 *   children  — cell content
 */
export default function DraggableColumnHeader({ index, column, onMove, children }) {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    hover(item) {
      if (item.index === index) return;
      onMove(item.index, index);
      item.index = index;
    },
  });

  drag(drop(ref));

  return (
    <TableCell
      ref={ref}
      sx={{
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        fontWeight: 600,
        fontSize: '0.8rem',
        py: 1,
        px: 1,
        bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DragIndicatorIcon sx={{ fontSize: 16, color: 'grey.400' }} />
        {children}
      </Box>
    </TableCell>
  );
}
